import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Edit3, Eye } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { ADMIN } from "@/constants/testIds";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { SellerPreviewModal } from "@/components/preview/SellerPreviewModal";
import { QuestionPreview } from "@/components/preview/ExamPreview";

const CATS = [
  { id: "sales", es: "Ventas", en: "Sales" },
  { id: "service", es: "Servicio", en: "Service" },
  { id: "financing", es: "Financiamiento", en: "Financing" },
  { id: "custom", es: "Personalizado", en: "Custom" },
];

const emptyForm = () => ({
  category: "sales", topic: "",
  prompt_es: "", prompt_en: "",
  options: [
    { id: "A", es: "", en: "" },
    { id: "B", es: "", en: "" },
    { id: "C", es: "", en: "" },
    { id: "D", es: "", en: "" },
  ],
  correct: "A",
  explanation_es: "", explanation_en: "",
  mini_story_es: "", mini_story_en: "",
});

export default function Questions() {
  const { t, lang } = useLang();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/questions", { params: { category: filter === "all" ? undefined : filter } });
    setList(data);
  };
  useEffect(() => { load(); }, [filter]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };
  const openEdit = (q) => {
    setEditing(q.question_id);
    setForm({
      category: q.category, topic: q.topic,
      prompt_es: q.prompt_es, prompt_en: q.prompt_en,
      options: q.options,
      correct: q.correct,
      explanation_es: q.explanation_es, explanation_en: q.explanation_en,
      mini_story_es: q.mini_story_es || "", mini_story_en: q.mini_story_en || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/admin/questions/${editing}`, form);
      } else {
        await api.post(`/admin/questions`, form);
      }
      toast.success(lang === "es" ? "Guardado" : "Saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(lang === "es" ? "¿Eliminar?" : "Delete?")) return;
    await api.delete(`/admin/questions/${id}`);
    load();
  };

  const setOpt = (i, field, val) => {
    setForm((f) => ({ ...f, options: f.options.map((o, j) => (j === i ? { ...o, [field]: val } : o)) }));
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-black text-4xl">{t("questions")}</h1>
        <div className="flex gap-3 items-center">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {CATS.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c[lang]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="brutal-btn bg-canales-red text-white px-4 py-2">
                <Plus className="w-4 h-4 mr-1" /> {t("add")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl brutal border-black bg-white max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display font-black text-2xl">
                  {editing ? t("edit") : t("add")}
                </DialogTitle>
              </DialogHeader>
              <div className="grid gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t("category")}</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATS.map((c) => <SelectItem key={c.id} value={c.id}>{c[lang]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Topic</Label>
                    <Input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Prompt ES</Label>
                    <Textarea value={form.prompt_es} onChange={(e) => setForm({ ...form, prompt_es: e.target.value })} />
                  </div>
                  <div>
                    <Label>Prompt EN</Label>
                    <Textarea value={form.prompt_en} onChange={(e) => setForm({ ...form, prompt_en: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {form.options.map((o, i) => (
                    <div key={o.id} className="grid grid-cols-[40px_1fr_1fr] gap-2 items-center">
                      <div className={`w-9 h-9 rounded-full border-2 border-black flex items-center justify-center font-display font-black ${form.correct === o.id ? "bg-green-400" : "bg-white"}`}>
                        <button onClick={() => setForm({ ...form, correct: o.id })}>{o.id}</button>
                      </div>
                      <Input placeholder="ES" value={o.es} onChange={(e) => setOpt(i, "es", e.target.value)} />
                      <Input placeholder="EN" value={o.en} onChange={(e) => setOpt(i, "en", e.target.value)} />
                    </div>
                  ))}
                  <div className="text-xs text-neutral-600 font-body">
                    {lang === "es" ? "Clic en la letra para marcar la respuesta correcta." : "Click the letter to mark the correct answer."}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Explanation ES</Label>
                    <Textarea value={form.explanation_es} onChange={(e) => setForm({ ...form, explanation_es: e.target.value })} />
                  </div>
                  <div>
                    <Label>Explanation EN</Label>
                    <Textarea value={form.explanation_en} onChange={(e) => setForm({ ...form, explanation_en: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Mini-story ES</Label>
                    <Textarea value={form.mini_story_es} onChange={(e) => setForm({ ...form, mini_story_es: e.target.value })} />
                  </div>
                  <div>
                    <Label>Mini-story EN</Label>
                    <Textarea value={form.mini_story_en} onChange={(e) => setForm({ ...form, mini_story_en: e.target.value })} />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setPreviewOpen(true)} data-testid="question-preview-btn" className="brutal-btn bg-white text-black px-4 py-2">
                  <Eye className="w-4 h-4 mr-1" /> {lang === "es" ? "Vista previa" : "Preview"}
                </Button>
                <Button data-testid={ADMIN.saveQuestion} onClick={save} className="brutal-btn bg-canales-blue text-white px-4 py-2">
                  {t("save")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-6">
        {(() => {
          // Group by category, preserving DB order within each group
          const groups = {};
          list.forEach((q) => {
            const cat = q.category || "custom";
            (groups[cat] ||= []).push(q);
          });
          const order = ["sales", "service", "financing", "custom"];
          const ordered = [
            ...order.filter((c) => groups[c]),
            ...Object.keys(groups).filter((c) => !order.includes(c)),
          ];
          const catLabel = (id) => CATS.find((c) => c.id === id)?.[lang] || id;
          const catColor = { sales: "#005BBB", service: "#E3000F", financing: "#FFB800", custom: "#8B5CF6" };
          if (list.length === 0) {
            return (
              <div className="brutal p-8 bg-white text-center font-body text-neutral-500">
                {lang === "es" ? "Sin preguntas todavía" : "No questions yet"}
              </div>
            );
          }
          return ordered.map((cat) => (
            <section key={cat} data-testid={`questions-group-${cat}`}>
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full border-2 border-black text-xs font-display font-black text-white uppercase tracking-widest"
                  style={{ background: catColor[cat] || "#333" }}
                >
                  {catLabel(cat)}
                </span>
                <div className="text-sm font-display font-bold text-neutral-600">
                  {groups[cat].length} {lang === "es" ? "preguntas" : "questions"}
                </div>
                <div className="flex-1 h-1 border-b-2 border-dashed border-neutral-300" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {groups[cat].map((q) => (
                  <div key={q.question_id} data-testid={ADMIN.questionRow(q.question_id)} className="brutal p-5 bg-white">
                    <div className="text-xs font-display font-bold uppercase tracking-widest text-canales-red">
                      {q.category} · {q.topic}
                    </div>
                    <h3 className="font-display font-black text-lg mt-1">{q[`prompt_${lang}`]}</h3>
                    <ul className="mt-2 space-y-1 text-sm font-body">
                      {q.options.map((o) => (
                        <li key={o.id} className={o.id === q.correct ? "font-bold text-green-700" : ""}>
                          {o.id}. {o[lang]} {o.id === q.correct ? " ✓" : ""}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(q)} className="brutal-btn bg-canales-yellow text-black px-3 py-1 text-sm">
                        <Edit3 className="w-4 h-4 mr-1" /> {t("edit")}
                      </Button>
                      <Button size="sm" onClick={() => remove(q.question_id)} className="brutal-btn bg-red-500 text-white px-3 py-1 text-sm">
                        <Trash2 className="w-4 h-4 mr-1" /> {t("delete")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ));
        })()}
      </div>

      <SellerPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title={lang === "es" ? "Vista previa — así ve la pregunta el vendedor" : "Preview — how the seller sees the question"}
      >
        <QuestionPreview form={form} lang={lang} />
      </SellerPreviewModal>
    </motion.div>
  );
}
